/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {ThemeProvider} from '@emotion/react'
import PropTypes from 'prop-types'
import React from 'react'
import {
    extendTheme,
    useChakra,
    useMultiStyleConfig,
    Alert,
    AlertDescription,
    AlertTitle,
    CloseButton,
    createStandaloneToast
} from '@chakra-ui/react'

const DEFAULTS = {
    duration: 5000,
    isClosable: true,
    position: 'bottom',
    variant: 'solid'
}

const StokkeToast = ({
    action,
    isClosable = true,
    status = 'info',
    title,
    description,
    // just to remove from ...rest
    // eslint-disable-next-line react/prop-types
    position,
    onClose,
    ...rest
}) => {
    if (!(title || description)) return ''
    return (
        <Alert status={status} {...rest}>
            {title && <AlertTitle>{title}</AlertTitle>}
            {description && <AlertDescription>{description}</AlertDescription>}
            {action}
            {isClosable && <CloseButton onClick={onClose} />}
        </Alert>
    )
}

StokkeToast.propTypes = {
    action: PropTypes.any,
    colorScheme: PropTypes.string,
    isClosable: PropTypes.bool,
    status: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    onClose: PropTypes.func
}

/**
 * Display a toast message on the screen.
 * This is a custom hook to handle showing toasts in the app, preventing duplicate toasts and to add action elements
 * to toasts when required. It supports all props supported by Chakra toast.
 *
 * @param {string} title Message text to be displayed in toast
 * @param {string} id - id provided to the toast to avoid duplicate toast ids, use it if multiple toasts are needed
 * @param {string} status Semantic state of the toast - success | error | info | warning
 * @param {node} [action] Optional component to be displayed in the toast (eg. Button to allow user to perform action)
 * @param {string} [position] The placement of the toast on screen
 * @param {number} [duration] The delay before the toast hides (in milliseconds)
 */
export function useToast(options) {
    const {theme, setColorMode, toggleColorMode, colorMode} = useChakra()
    // use the component theme to define a subtheme to be used inside the toast
    const styles = useMultiStyleConfig('Toast', options)
    return React.useMemo(() => {
        // create the subtheme based on the app theme with the overrides defined in Toast.theme
        const toastTheme = extendTheme(theme, styles.theme)
        const toast = createStandaloneToast({
            theme: toastTheme,
            colorMode,
            setColorMode,
            toggleColorMode,
            defaultOptions: {
                // some functional defaults
                ...DEFAULTS,
                // allows to define toast container styles on the theme
                containerStyle: styles.container,
                ...options
            }
        })

        // a render function creator to pass all the options and wrap in providers
        // eslint-disable-next-line react/display-name
        const wrapWithProviders = (opts) => (props) => {
            return (
                // Although a custom theme is provided to createStandaloneToast it is missing
                // when rendering StokkeToast, so it's solved by manually adding the theme provider
                // This is useful for other providers as well, since the ToastManager is rendered via portal
                // and the toast doesn't have access to intl provider
                <ThemeProvider theme={toastTheme}>
                    <StokkeToast {...DEFAULTS} {...opts} {...props} />
                </ThemeProvider>
            )
        }

        const toastImpl = (opts) =>
            toast({
                render: wrapWithProviders(opts),
                ...opts
            })

        toastImpl.update = (opts) =>
            toast.update({
                render: wrapWithProviders(opts),
                ...opts
            })

        toastImpl.close = toast.close
        toastImpl.closeAll = toast.closeAll
        toastImpl.isActive = toast.isActive

        return toastImpl
    }, [theme, setColorMode, toggleColorMode, colorMode, options, styles])
}
